import { Style, Fill, Stroke, Text, Circle, Icon } from 'ol/style';

/**
 * Simple offline vector style based on Mapnik/TH Carto colors
 * Designed for GeoJSON features from vector_extractor.py
 */

// Import sprite files from local assets
import spriteJsonData from '../assets/sprites/sprite.json';
import spritePng from '../assets/sprites/sprite.png';
import sprite2xPng from '../assets/sprites/sprite@2x.png';

// Sprite configuration
let spriteData = spriteJsonData; // Load from imported JSON
let spriteImage = null;
let spriteImage2x = null;
let spritesLoaded = false;
let spriteLoadCallbacks = [];
let hidePoiIconsWhileInteracting = false;
let spritePreloadStarted = false;
let retinaSpritesEnabled = true;
let forceOneXSpritesResolved = false;
let forceOneXSprites = false;

const POI_STYLE_CACHE_MAX = 2048;
const poiStyleCache = new Map();

function shouldForceOneXSprites() {
  if (forceOneXSpritesResolved) return forceOneXSprites;
  forceOneXSpritesResolved = true;

  if (typeof window === 'undefined') {
    forceOneXSprites = true;
    return forceOneXSprites;
  }

  const coarsePointer = typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  const narrowViewport = window.innerWidth <= 1024;
  const android = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');
  const lowMemory = typeof navigator !== 'undefined'
    && Number.isFinite(navigator.deviceMemory)
    && navigator.deviceMemory <= 4;

  forceOneXSprites = coarsePointer || narrowViewport || android || lowMemory;
  return forceOneXSprites;
}

function getCachedPoiStyle(cacheKey, buildStyle) {
  const existing = poiStyleCache.get(cacheKey);
  if (existing) return existing;

  if (poiStyleCache.size >= POI_STYLE_CACHE_MAX) {
    poiStyleCache.clear();
  }

  const created = buildStyle();
  poiStyleCache.set(cacheKey, created);
  return created;
}

function buildPoiStyleKey({
  mode,
  iconName,
  iconColor,
  showPoiText,
  textValue,
  isHighPriority,
  useRetina
}) {
  return [
    mode,
    iconName || '',
    iconColor || '',
    isHighPriority ? '1' : '0',
    useRetina ? '2x' : '1x',
    showPoiText ? 'text' : 'no-text',
    showPoiText ? (textValue || '') : ''
  ].join('|');
}

/**
 * Load sprite metadata (now using imported data)
 */
async function loadSpriteData() {
  if (spriteData) return spriteData;

  try {
    // Data is already imported, just return it
    spriteData = spriteJsonData;
    checkSpritesLoaded(); // Check if everything is loaded now
    return spriteData;
  } catch (error) {
    console.error('Failed to load sprite data:', error);
    return null;
  }
}

/**
 * Preload sprite images (now using imported images from webpack)
 */
export function preloadSprites() {
  if (spritePreloadStarted || spritesLoaded) {
    return;
  }
  spritePreloadStarted = true;

  // Load sprite metadata
  loadSpriteData();

  retinaSpritesEnabled = !shouldForceOneXSprites();

  // Preload standard resolution image using imported path
  if (!spriteImage) {
    spriteImage = new Image();
    spriteImage.onload = () => {
      checkSpritesLoaded();
    };
    spriteImage.onerror = (e) => {
      console.error('Failed to load sprite image (1x):', e);
    };
    spriteImage.src = spritePng;
  }

  // Preload retina resolution image using imported path
  if (!retinaSpritesEnabled) {
    checkSpritesLoaded();
    return;
  }
  if (!spriteImage2x) {
    spriteImage2x = new Image();
    spriteImage2x.onload = () => {
      checkSpritesLoaded();
    };
    spriteImage2x.onerror = (e) => {
      console.error('Failed to load sprite image (2x):', e);
    };
    spriteImage2x.src = sprite2xPng;
  }
}

function checkSpritesLoaded() {
  const hasBase = Boolean(spriteImage && spriteImage.complete);
  const hasRetina = !retinaSpritesEnabled || Boolean(spriteImage2x && spriteImage2x.complete);

  if (hasBase && hasRetina && spriteData) {
    spritesLoaded = true;

    // Notify any registered callbacks that sprites are ready
    spriteLoadCallbacks.forEach(callback => {
      try {
        callback();
      } catch (e) {
        console.error('Error in sprite load callback:', e);
      }
    });
    spriteLoadCallbacks = [];
  }
}

/**
 * Register a callback to be called when sprites are loaded
 * If sprites are already loaded, callback is called immediately
 */
export function onSpritesLoaded(callback) {
  if (spritesLoaded) {
    callback();
  } else {
    spriteLoadCallbacks.push(callback);
  }
}

export function setOfflineInteracting(isInteracting) {
  hidePoiIconsWhileInteracting = Boolean(isInteracting);
}

// Preload sprites on demand to avoid blocking initial app load.

/**
 * Map POI category/amenity to sprite icon name
 */
function getPoiIconName(properties) {
  const { amenity, shop, tourism, historic, leisure, railway, category } = properties;

  // Direct mapping for common amenities
  const amenityIcons = {
    'restaurant': 'restaurant',
    'cafe': 'cafe',
    'fast_food': 'fast_food',
    'pub': 'pub',
    'bar': 'bar',
    'bank': 'bank',
    'atm': 'atm',
    'pharmacy': 'pharmacy',
    'hospital': 'hospital',
    'doctors': 'doctors',
    'dentist': 'dentist',
    'police': 'police',
    'post_office': 'post_office',
    'post_box': 'post_box',
    'telephone': 'telephone',
    'fuel': 'fuel',
    'parking': 'parking',
    'bicycle_parking': 'bicycle_parking',
    'toilets': 'toilets',
    'taxi': 'taxi',
    'bus_station': 'bus_station',
    'ferry_terminal': 'ferry',
    'place_of_worship': 'place_of_worship',
    'grave_yard': 'grave_yard_generic',
    'fountain': 'fountain',
    'bench': 'bench',
    'waste_basket': 'waste_basket',
    'recycling': 'recycling',
    'drinking_water': 'drinking_water',
    'shelter': 'shelter',
    'townhall': 'town_hall',
    'town_hall': 'town_hall',
    'library': 'library',
    'fire_station': 'firestation',
    'community_centre': 'community_centre',
    'theatre': 'theatre',
    'cinema': 'cinema',
    'arts_centre': 'arts_centre',
    'nightclub': 'nightclub',
    'casino': 'casino',
    'charging_station': 'charging_station',
    'car_wash': 'car_wash',
    'veterinary': 'veterinary',
    'marketplace': 'marketplace',
    'bbq': 'bbq',
    'picnic_site': 'picnic',
    'viewpoint': 'viewpoint'
  };

  // Shop icons
  const shopIcons = {
    'supermarket': 'supermarket',
    'convenience': 'convenience',
    'bakery': 'bakery',
    'butcher': 'butcher',
    'clothes': 'clothes',
    'hairdresser': 'hairdresser',
    'beauty': 'beauty',
    'florist': 'florist',
    'greengrocer': 'greengrocer',
    'bicycle': 'bicycle',
    'car': 'car',
    'car_repair': 'car_repair',
    'car_parts': 'car_parts',
    'books': 'bookmaker',
    'gift': 'gift',
    'jewelry': 'jewelry',
    'shoes': 'shoes',
    'alcohol': 'alcohol',
    'beverages': 'beverages',
    'tobacco': 'tobacco',
    'chemist': 'chemist',
    'electronics': 'electronics',
    'mobile_phone': 'mobile_phone',
    'computer': 'computer',
    'toys': 'toys',
    'sports': 'sports',
    'furniture': 'furniture',
    'hardware': 'diy',
    'doityourself': 'diy',
    'stationery': 'stationery',
    'laundry': 'laundry',
    'department_store': 'department_store',
    'kiosk': 'newsagent',
    'newsagent': 'newsagent',
    'optician': 'optician',
    'pet': 'pet',
    'photo': 'photo',
    'seafood': 'seafood',
    'second_hand': 'second_hand',
    'tyres': 'tyres',
    'variety_store': 'variety_store',
    'video': 'video'
  };

  // Tourism icons
  const tourismIcons = {
    'hotel': 'hotel',
    'motel': 'motel',
    'hostel': 'hostel',
    'guest_house': 'guest_house',
    'chalet': 'chalet',
    'apartment': 'apartment',
    'museum': 'museum',
    'artwork': 'artwork',
    'attraction': 'information',
    'viewpoint': 'viewpoint',
    'information': 'information',
    'picnic_site': 'picnic',
    'camp_site': 'camping',
    'caravan_site': 'caravan_park',
    'alpine_hut': 'alpinehut',
    'wilderness_hut': 'wilderness_hut'
  };

  // Historic icons
  const historicIcons = {
    'monument': 'monument',
    'memorial': 'memorial',
    'castle': 'castle',
    'fort': 'fort',
    'ruins': 'archaeological_site',
    'archaeological_site': 'archaeological_site',
    'wayside_cross': 'cross',
    'wayside_shrine': 'shrine',
    'battlefield': 'information',
    'manor': 'manor',
    'palace': 'palace'
  };

  // Leisure icons
  const leisureIcons = {
    'playground': 'playground',
    'park': 'information',
    'garden': 'garden_centre',
    'sports_centre': 'sports',
    'stadium': 'sports',
    'swimming_pool': 'sports',
    'golf_course': 'golf',
    'miniature_golf': 'miniature_golf',
    'water_park': 'water_park',
    'beach_resort': 'beach_resort',
    'fishing': 'fishing',
    'marina': 'boat_rental',
    'dog_park': 'dog_park',
    'fitness_centre': 'fitness',
    'fitness_station': 'fitness',
    'sauna': 'sauna'
  };

  // Railway/transit icons
  const railwayIcons = {
    'station': 'rail',  // Train station
    'halt': 'rail',  // Small train stop
    'tram_stop': 'tram',  // Tram stop
    'subway_entrance': 'entrance'  // Subway/metro entrance
  };

  // Check in order of specificity
  if (railway && railwayIcons[railway]) return railwayIcons[railway];
  if (amenity && amenityIcons[amenity]) return amenityIcons[amenity];
  if (shop && shopIcons[shop]) return shopIcons[shop];
  if (tourism && tourismIcons[tourism]) return tourismIcons[tourism];
  if (historic && historicIcons[historic]) return historicIcons[historic];
  if (leisure && leisureIcons[leisure]) return leisureIcons[leisure];

  // Default icon
  return 'information';
}

/**
 * Convert OpenLayers resolution to zoom level
 */
function resolutionToZoom(resolution) {
  const maxResolution = 156543.03392804097;
  return Math.log2(maxResolution / resolution);
}

function isTruthyOsmFlag(value) {
  if (value === undefined || value === null) return false;
  if (value === false) return false;
  const normalized = String(value).toLowerCase();
  return normalized !== 'no' && normalized !== 'false' && normalized !== '0';
}

/**
 * Get road color for offline vector rendering
 * Tuned toward the lighter raster look used online.
 */
function getRoadColor(highway) {
  if (!highway) return '#fbfbfb';

  const colors = {
    // Keep major roads only slightly warmer than local streets
    'motorway': '#f6f2e8',
    'motorway_link': '#f6f2e8',
    'trunk': '#f6f2e8',
    'trunk_link': '#f6f2e8',
    'primary': '#f7f4ea',
    'primary_link': '#f7f4ea',

    // Secondary roads are still light, but a touch warmer than residential streets
    'secondary': '#faf7ef',
    'secondary_link': '#faf7ef',

    // Most streets are near-white in the raster style
    'tertiary': '#fbfbfb',
    'tertiary_link': '#fbfbfb',
    'residential': '#fbfbfb',
    'unclassified': '#fbfbfb',
    'living_street': '#fbfbfb',
    'service': '#fbfbfb',
    'road': '#fbfbfb',

    // Pedestrian/minor paths are slightly dimmer than roads so they don't dominate
    'pedestrian': '#f4f4f4',
    'footway': '#f2f2f2',
    'cycleway': '#f2f2f2',
    'path': '#f0f0f0',
    'steps': '#f0f0f0',
    'track': '#efefef'
  };

  return colors[highway] || '#fbfbfb';
}

/**
 * Get road width based on highway type and zoom
 * Widths tuned to better match the online raster visual weight.
 */
function getRoadWidth(highway, zoom) {
  if (!highway) return 1.5;

  // Base widths at zoom 15
  const baseWidths = {
    'motorway': 11,
    'motorway_link': 7,
    'trunk': 10,
    'trunk_link': 7,
    'primary': 9,
    'primary_link': 6.5,
    'secondary': 7.5,
    'secondary_link': 6,
    'tertiary': 5.5,
    'tertiary_link': 4.5,
    'residential': 4.2,
    'unclassified': 4.2,
    'living_street': 3.8,
    'service': 3,
    'pedestrian': 3.2,
    'footway': 2,
    'cycleway': 2,
    'path': 1.6,
    'steps': 1.8,
    'track': 2
  };

  const baseWidth = baseWidths[highway] || 4;

  // Scale based on zoom
  let scale = 1;
  if (zoom < 14) {
    scale = 0.45;
  } else if (zoom < 15) {
    scale = 0.65;
  } else if (zoom < 16) {
    scale = 0.9;
  } else if (zoom < 17) {
    scale = 1.05;
  } else if (zoom < 18) {
    scale = 1.2;
  } else if (zoom < 19) {
    scale = 1.45;
  } else {
    scale = 1.7;
  }

  return baseWidth * scale;
}

/**
 * Get road casing color (darker outline)
 */
function getRoadCasingColor(highway) {
  if (!highway) return '#d0d3d6';

  const majorRoads = ['motorway', 'trunk', 'primary', 'secondary', 'tertiary',
                      'motorway_link', 'trunk_link', 'primary_link', 'secondary_link', 'tertiary_link'];

  if (majorRoads.includes(highway)) {
    return '#bcc1c6';
  }

  return '#d7dbdf';
}

/**
 * Create style for a feature
 */
export function createSimpleOfflineStyle(feature, resolution) {
  const properties = feature.getProperties();
  const layer =
    properties.layer ||
    properties.source_layer ||
    feature.get('layer') ||
    feature.get('sourceLayer') ||
    feature.get('source_layer');
  const zoom = resolutionToZoom(resolution);
  const geometryType = feature.getGeometry?.()?.getType?.();

  if (layer === 'land') {
    return new Style({
      fill: new Fill({
        color: '#f3f1eb'
      }),
      zIndex: 0
    });
  }

  if (layer === 'coastline') {
    return new Style({
      stroke: new Stroke({
        color: '#5a5a5a',
        width: zoom >= 12 ? 1.0 : 0.6
      }),
      zIndex: 5
    });
  }

  // Roads styling
  if (layer === 'roads') {
    const highway =
      properties.highway ||
      properties.class ||
      properties.subclass ||
      properties['area:highway'] ||
      properties.area_highway;
    const roadColor = getRoadColor(highway);
    const roadWidth = getRoadWidth(highway, zoom);
    const casingColor = getRoadCasingColor(highway);
    const isBridge = isTruthyOsmFlag(properties.bridge);
    const isTunnel = isTruthyOsmFlag(properties.tunnel);
    const hasEmbankment = isTruthyOsmFlag(properties.embankment);
    const isRoadArea = geometryType === 'Polygon' || geometryType === 'MultiPolygon';

    // Pedestrian squares/plazas are often polygons in the roads layer. Fill them to match raster map plazas.
    if (isRoadArea) {
      const isPedestrianArea =
        highway === 'pedestrian' ||
        highway === 'footway' ||
        highway === 'path' ||
        highway === 'steps' ||
        properties['area:highway'] === 'pedestrian' ||
        properties.area_highway === 'pedestrian' ||
        isTruthyOsmFlag(properties.pedestrian);

      const fillColor = isPedestrianArea ? '#e7e4db' : '#f2f1ec';
      const strokeColor = isPedestrianArea ? '#cfcabf' : '#e0ddd4';

      const styles = [
        new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({
            color: strokeColor,
            width: zoom >= 17 ? 0.8 : 0.5
          }),
          // Keep plazas visible, but below water polygons so canals cut through correctly.
          zIndex: isPedestrianArea ? 24 : 18
        })
      ];

      if (properties.name && isPedestrianArea && zoom >= 15) {
        styles.push(new Style({
          text: new Text({
            text: properties.name,
            font: '10px sans-serif',
            fill: new Fill({ color: '#4e4e4e' }),
            stroke: new Stroke({ color: '#fafafa', width: 3 }),
            textAlign: 'center'
          }),
          zIndex: 101
        }));
      }

      return styles;
    }

    // Determine road importance for zIndex layering
    const roadImportance = {
      'motorway': 90,
      'motorway_link': 89,
      'trunk': 88,
      'trunk_link': 87,
      'primary': 86,
      'primary_link': 85,
      'secondary': 84,
      'secondary_link': 83,
      'tertiary': 82,
      'tertiary_link': 81,
      'residential': 80,
      'unclassified': 79,
      'living_street': 78,
      'service': 77,
      'pedestrian': 76,
      'footway': 75,
      'cycleway': 74,
      'path': 73,
      'steps': 72,
      'track': 71
    };

    let roadZIndex = roadImportance[highway] || 70;
    if (isBridge) roadZIndex += 8;
    if (isTunnel) roadZIndex -= 8;

    // Determine casing width based on road type
    let casingWidth = 1;
    const majorRoads = ['motorway', 'trunk', 'primary', 'secondary', 'tertiary',
                        'motorway_link', 'trunk_link', 'primary_link', 'secondary_link', 'tertiary_link'];

    if (majorRoads.includes(highway)) {
      casingWidth = 2; // Wider casing for major roads
    } else {
      casingWidth = 1; // Thinner casing for minor roads/footpaths
    }

    // All roads get casing (outline) at zoom 14+
    if (zoom >= 14) {
      // Return array with casing (drawn first), fill, and optional text
      const styles = [];

      if (isBridge) {
        styles.push(new Style({
          stroke: new Stroke({
            color: 'rgba(120, 120, 120, 0.22)',
            width: roadWidth + casingWidth + 1.5,
            lineCap: 'butt',
            lineJoin: 'round'
          }),
          zIndex: roadZIndex - 1
        }));
      }

      if (hasEmbankment && zoom >= 15) {
        styles.push(new Style({
          stroke: new Stroke({
            color: 'rgba(155, 145, 125, 0.35)',
            width: roadWidth + casingWidth + 2.5,
            lineCap: 'butt',
            lineJoin: 'round',
            lineDash: [2, 5]
          }),
          zIndex: roadZIndex - 2
        }));
      }

      styles.push(
        new Style({
          stroke: new Stroke({
            color: isTunnel ? '#d8dade' : casingColor,
            width: roadWidth + casingWidth,
            lineCap: 'butt',  // Flat caps
            lineJoin: 'round',  // Rounded joins
            lineDash: isTunnel ? [6, 4] : undefined
          }),
          zIndex: roadZIndex
        }),
        new Style({
          stroke: new Stroke({
            color: isTunnel ? '#f1f1f1' : roadColor,
            width: roadWidth,
            lineCap: 'butt',
            lineJoin: 'round',
            lineDash: isTunnel ? [6, 4] : undefined
          }),
          zIndex: roadZIndex
        })
      );

      // Add street name label for major roads at zoom 14+
      if (properties.name && majorRoads.includes(highway) && zoom >= 14) {
        styles.push(new Style({
          text: new Text({
            text: properties.name,
            font: '10px sans-serif',
            fill: new Fill({ color: '#555' }),
            stroke: new Stroke({ color: '#f8f8f8', width: 2.5 }),
            placement: 'line',
            maxAngle: Math.PI / 4,
            overflow: false
          }),
          zIndex: 100 // High priority for street names
        }));
      }

      // Add street name label for minor roads at zoom 15+
      if (properties.name && !majorRoads.includes(highway) && zoom >= 15) {
        styles.push(new Style({
          text: new Text({
            text: properties.name,
            font: '9px sans-serif',
            fill: new Fill({ color: '#666' }),
            stroke: new Stroke({ color: '#f8f8f8', width: 2 }),
            placement: 'line',
            maxAngle: Math.PI / 4,
            overflow: false
          }),
          zIndex: 100 // High priority for street names
        }));
      }

      return styles;
    }

    // At lower zoom levels (< 14), simple roads without casing
    const simpleStyle = new Style({
      stroke: new Stroke({
        color: roadColor,
        width: roadWidth,
        lineCap: 'butt',
        lineJoin: 'round'
      }),
      zIndex: roadZIndex
    });

    return simpleStyle;
  }

  // Buildings styling
  if (layer === 'buildings') {
    const building = properties.building;

    // Default building color - light beige
    let fillColor = '#e6e1bd';
    let strokeColor = '#d3cda8';

    // Special building types get different colors
    // Note: These should match if buildings have building=hospital, building=church etc.
    if (building === 'hospital' || building === 'clinic') {
      fillColor = '#eadbdb';
      strokeColor = '#d1bcbc';
    } else if (building === 'school' || building === 'university' || building === 'college') {
      fillColor = '#ece9cf';
      strokeColor = '#d4d1b8';
    } else if (building === 'church' || building === 'cathedral' || building === 'mosque' || building === 'synagogue' || building === 'temple') {
      fillColor = '#d8cdc7';
      strokeColor = '#bfb4ad';
    } else if (building === 'train_station' || building === 'transportation') {
      fillColor = '#e3e0e8';
      strokeColor = '#cbc7d2';
    }

    return new Style({
      fill: new Fill({
        color: fillColor
      }),
      stroke: new Stroke({
        color: strokeColor,
        width: 0.4
      }),
      zIndex: 20
    });
  }

  // Water styling (Mapnik color: #a0c8f0)
  if (layer === 'water') {
    return new Style({
      fill: new Fill({
        color: '#a9cfe4'
      }),
      stroke: new Stroke({
        color: '#95bfd8',
        width: 0.35
      }),
      // Keep water above land/plaza fills, but below roads/railways/labels.
      zIndex: 30
    });
  }

  // Natural features - parks, forests, grassland
  if (layer === 'natural') {
    const natural = properties.natural;
    const leisure = properties.leisure;

    let fillColor = '#9dcfaf';

    if (natural === 'wood' || leisure === 'park' || leisure === 'nature_reserve') {
      fillColor = '#86bb96';
    } else if (natural === 'grassland' || leisure === 'garden' || natural === 'grass') {
      fillColor = '#a6d3b2';
    } else if (natural === 'scrub' || natural === 'heath') {
      fillColor = '#a6d3b2';
    }

    return new Style({
      fill: new Fill({
        color: fillColor
      }),
      stroke: new Stroke({
        color: '#96be93',
        width: 0.3
      }),
      zIndex: 10
    });
  }

  // Landuse features
  if (layer === 'landuse') {
    const landuse = properties.landuse;

    const colors = {
      'residential': '#eceae1',
      'commercial': '#ebe8de',
      'industrial': '#e7e1e9',
      'retail': '#ede8de',
      'railway': '#e6e0e7',
      'cemetery': '#bfd7c2',
      'construction': '#d4d0bf'
    };

    return new Style({
      fill: new Fill({
        color: colors[landuse] || '#e0dfdf'
      }),
      stroke: new Stroke({
        color: '#ddd9ce',
        width: 0.3
      }),
      zIndex: 5
    });
  }

  // Railway styling (tram, train, subway, etc.)
  if (layer === 'railways') {
    const railway = properties.railway;
    const isBridge = isTruthyOsmFlag(properties.bridge);
    const isTunnel = isTruthyOsmFlag(properties.tunnel);

    // Different styles for different railway types
    let railwayColor = '#666666';  // Default dark grey for rail
    let railwayWidth = 2;
    let railwayDash = null;  // Solid line by default
    let casingColor = '#ffffff';  // White casing by default

    if (railway === 'tram') {
      railwayColor = '#d97fd9';  // Pink/magenta for tram
      railwayWidth = 2;
      casingColor = '#ffffff';  // White casing
    } else if (railway === 'subway') {
      railwayColor = '#0066cc';  // Blue for subway
      railwayWidth = 2;
      railwayDash = [5, 5];  // Dashed for underground
      casingColor = '#003366';  // Dark blue casing
    } else if (railway === 'light_rail') {
      railwayColor = '#cc66ff';  // Purple for light rail
      railwayWidth = 2;
      casingColor = '#ffffff';  // White casing
    } else if (railway === 'rail') {
      railwayColor = '#666666';  // Dark grey for train
      railwayWidth = 2.5;
      railwayDash = [8, 4];  // Dashed line for trains
      casingColor = '#333333';  // Dark grey/black casing for trains
    } else if (railway === 'narrow_gauge') {
      railwayColor = '#888888';  // Medium grey
      railwayWidth = 1.5;
      railwayDash = [6, 3];  // Dashed
      casingColor = '#555555';  // Dark grey casing
    } else if (railway === 'funicular' || railway === 'monorail') {
      railwayColor = '#999999';  // Light grey
      railwayWidth = 1.5;
      railwayDash = [3, 3];
      casingColor = '#666666';  // Medium grey casing
    }

    // Only show railways at zoom 13+
    if (zoom < 13) {
      return null;
    }

    // Create railway line style with casing
    const styles = [];
    const railwayZIndex = isBridge ? 88 : (isTunnel ? 78 : 85);

    if (isBridge) {
      styles.push(new Style({
        stroke: new Stroke({
          color: 'rgba(110, 110, 110, 0.2)',
          width: railwayWidth + 3,
          lineCap: 'round',
          lineJoin: 'round'
        }),
        zIndex: railwayZIndex - 1
      }));
    }

    // Add casing for better visibility at higher zooms
    if (zoom >= 15) {
      styles.push(new Style({
        stroke: new Stroke({
          color: isTunnel ? '#d9d9d9' : casingColor,
          width: railwayWidth + 2,
          lineCap: 'round',
          lineJoin: 'round',
          lineDash: isTunnel ? [4, 4] : undefined
        }),
        zIndex: railwayZIndex
      }));
    }

    // Main railway line
    styles.push(new Style({
      stroke: new Stroke({
        color: railwayColor,
        width: railwayWidth,
        lineCap: 'round',
        lineJoin: 'round',
        lineDash: isTunnel ? [4, 4] : railwayDash
      }),
      zIndex: railwayZIndex
    }));

    // Add railway name label at zoom 14+
    if (properties.name && zoom >= 14) {
      styles.push(new Style({
        text: new Text({
          text: properties.name,
          font: '9px sans-serif',
          fill: new Fill({ color: '#4f4f4f' }),
          stroke: new Stroke({ color: '#fafafa', width: 2 }),
          placement: 'line',
          maxAngle: Math.PI / 4,
          overflow: false
        }),
        zIndex: railwayZIndex + 10
      }));
    }

    return styles;
  }

  // Barriers styling (walls, fences, etc.)
  if (layer === 'barriers') {
    const barrier = properties.barrier;

    // Fences are more subtle (thinner)
    const isFence = barrier && (barrier.includes('fence') || barrier === 'hedge');

    // Scale barrier width with zoom - thinner overall, even thinner for fences
    let barrierWidth = 0.8;
    if (isFence) {
      if (zoom >= 17) {
        barrierWidth = 0.8;
      } else if (zoom >= 16) {
        barrierWidth = 0.6;
      } else {
        barrierWidth = 0.5;
      }
    } else {
      // Walls and other solid barriers
      if (zoom >= 17) {
        barrierWidth = 1.2;
      } else if (zoom >= 16) {
        barrierWidth = 1.0;
      } else if (zoom >= 15) {
        barrierWidth = 0.8;
      } else {
        barrierWidth = 0.6;
      }
    }

    return new Style({
      stroke: new Stroke({
        color: '#666666',  // Dark grey instead of black
        width: barrierWidth,
        lineCap: 'butt',
        lineJoin: 'miter'
      }),
      zIndex: 150  // Render above roads (roads are zIndex 100)
    });
  }

  const isPoiFeature =
    layer === 'pois' ||
    properties.type === 'poi' ||
    Boolean(
      properties.amenity ||
      properties.shop ||
      properties.tourism ||
      properties.historic ||
      properties.leisure ||
      ['station', 'halt', 'tram_stop', 'subway_entrance'].includes(properties.railway)
    );

  // POIs - use sprite icons, but fall back to circle markers when sprite data is unavailable.
  if (isPoiFeature) {
    if (hidePoiIconsWhileInteracting) {
      return null;
    }
    const iconName = getPoiIconName(properties);

    // High-priority POIs visible earlier to better match the raster map density.
    const highPriorityPOIs = ['atm', 'pharmacy', 'toilets', 'information'];
    const isTransitStop = properties.railway === 'station' ||
                          properties.railway === 'tram_stop' ||
                          properties.railway === 'halt' ||
                          properties.railway === 'subway_entrance';
    const isHighPriority = highPriorityPOIs.includes(properties.amenity) ||
                          (properties.tourism === 'information') ||
                          isTransitStop;

    // Keep POI density lower on mobile/offline for smoother pan/zoom.
    // High-priority POIs appear from z15, others from z18.
    if (isHighPriority && zoom < 15) {
      return null;
    }
    if (!isHighPriority && zoom < 18) {
      return null;
    }

    // Determine icon color based on POI type
    // Note: Sprites must be white/monochrome for color tinting to work properly
    let iconColor = '#724a09'; // Default brown color

    // Transit stops get special colors
    if (properties.railway === 'station') {
      iconColor = '#333333'; // Dark grey for train stations
    } else if (properties.railway === 'tram_stop') {
      iconColor = '#d97fd9'; // Pink/magenta for tram stops
    } else if (properties.railway === 'subway_entrance') {
      iconColor = '#0066cc'; // Blue for subway
    } else if (properties.amenity === 'pharmacy') {
      iconColor = '#778274'; // Green for pharmacies
    } else if (properties.amenity === 'atm') {
      iconColor = '#868179'; // Gray for ATMs
    } else if (properties.amenity === 'toilets') {
      iconColor = '#724a09'; // Brown for toilets
    } else if (properties.tourism === 'information' || properties.amenity === 'information') {
      iconColor = '#724a09'; // Brown for tourist info
    }

    const showPoiText = properties.name && (isHighPriority ? zoom >= 15 : zoom >= 18);
    const textValue = showPoiText ? properties.name : '';

    // Determine which sprite image to use based on device pixel ratio
    const pixelRatio = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
    const useRetina = retinaSpritesEnabled && pixelRatio > 1;
    const spriteImg = useRetina ? spriteImage2x : spriteImage;
    const spriteScale = useRetina ? 0.5 : 1;
    const hasSprite = Boolean(spriteImg && spriteImg.complete && spriteData && spriteData[iconName]);

    // Check if sprites are loaded and icon exists in sprite data
    if (!hasSprite) {
      const fallbackKey = buildPoiStyleKey({
        mode: 'fallback',
        iconName,
        iconColor,
        showPoiText,
        textValue,
        isHighPriority,
        useRetina: false
      });
      return getCachedPoiStyle(fallbackKey, () => new Style({
        image: new Circle({
          radius: isHighPriority ? 4 : 3,
          fill: new Fill({ color: iconColor }),
          stroke: new Stroke({ color: '#ffffff', width: 1.2 })
        }),
        text: showPoiText ? new Text({
          text: textValue,
          font: '10px sans-serif',
          fill: new Fill({ color: '#4d4d4d' }),
          stroke: new Stroke({ color: '#fafafa', width: 2.5 }),
          offsetY: 12,
          textAlign: 'center'
        }) : undefined,
        zIndex: 50
      }));
    }

    const iconData = spriteData[iconName];
    const spriteKey = buildPoiStyleKey({
      mode: 'sprite',
      iconName,
      iconColor,
      showPoiText,
      textValue,
      isHighPriority,
      useRetina
    });
    return getCachedPoiStyle(spriteKey, () => new Style({
      image: new Icon({
        img: spriteImg,
        imgSize: [spriteImg.width, spriteImg.height],
        size: [iconData.width, iconData.height],
        offset: [iconData.x, iconData.y],
        scale: spriteScale * (isHighPriority ? 0.95 : 0.9),
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        color: iconColor, // Tint white sprite icons with color
        opacity: 1.0
      }),
      text: showPoiText ? new Text({
        text: textValue,
        font: '10px sans-serif',
        fill: new Fill({ color: '#4d4d4d' }),
        stroke: new Stroke({ color: '#fafafa', width: 2.5 }),
        offsetY: 12,
        textAlign: 'center'
      }) : undefined,
      zIndex: 50 // Lower priority than street names (which have zIndex 100)
    }));
  }

  // Default fallback
  return new Style({
    fill: new Fill({
      color: 'rgba(200, 200, 200, 0.3)'
    }),
    stroke: new Stroke({
      color: '#888888',
      width: 1
    })
  });
}
